import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useWordPressConnection } from "../hooks/useWordPress";
import { useWebsite } from "../hooks/useWebsite";
import { useAccessibility } from "../hooks/useAccessibility";
import PageContainer from "../components/layout/PageContainer";
import LoadingSpinner from "../components/common/LoadingSpinner";
import WordPressConnectionsList from "../components/integrations/WordPressConnectionsList";
import WordPressConnectionCard from "../components/integrations/WordPressConnectionCard";
import WordPressActionsCard from "../components/integrations/WordPressActionsCard";
import WordPressGuideBanner from "../components/integrations/WordPressGuideBanner";
import ShopifyCard from "../components/integrations/ShopifyCard";
import ScanPlanner from "../components/integrations/ScanPlanner";
import WordPressModals, {
  SiteInfoModal,
  ViolationsModal,
  HTMLViewer,
} from "../components/integrations/WordPressModals";
import CompleteScanResponseModal from "../components/integrations/CompleteScanResponseModal";
import ConfirmDialog from "../components/common/ConfirmDialog";
import Modal from "../components/common/Modal";
import { Icon } from "@iconify/react";
import Button from "../components/common/Button";
import { shopifyService, SENTRYPRIME_URL } from "../services/api/shopifyService";

const Integrations = () => {
  const navigate = useNavigate();
  const { getAllWebsites } = useWebsite();
  const { scanUrl, isScanning: isScanningPageAccessibility } =
    useAccessibility();
  const {
    availableActions,
    isLoadingActions,
    testConnection,
    isTestingConnection,
    connectionError,
    getSiteInfo,
    isGettingSiteInfo,
    getPagesDirectly,
    isGettingPagesDirect,
    rollbackFixes,
    isRollingBack,
    getRollbackInfo,
    isGettingRollbackInfo,
    rollbackInfo,
    getViolations,
    isGettingViolations,
    disconnectSite,
    isDisconnectingSite,
    getAiFixes,
    isGettingAiFixes,
  } = useWordPressConnection();

  const [wordPressConnections, setWordPressConnections] = useState([]);
  const [shopifyConnections, setShopifyConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [selectedShopifyConnection, setSelectedShopifyConnection] =
    useState(null);
  const [shopifyPages, setShopifyPages] = useState([]);
  const [selectedShopifyPage, setSelectedShopifyPage] = useState(null);
  const [isGettingShopifyPages, setIsGettingShopifyPages] = useState(false);
  const [shopifyPagesError, setShopifyPagesError] = useState(null);
  const [wpModalOpen, setWpModalOpen] = useState(false);
  const [wpModalTitle, setWpModalTitle] = useState("");
  const [wpModalContent, setWpModalContent] = useState(null);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [disconnectTargetConnection, setDisconnectTargetConnection] =
    useState(null);
  const [scanResponseModalOpen, setScanResponseModalOpen] = useState(false);
  const [scanResponseData, setScanResponseData] = useState(null);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [rollbackPreviewData, setRollbackPreviewData] = useState(null);
  const [rollbackConnectionId, setRollbackConnectionId] = useState(null);
  const [rollbackSelectedSelectors, setRollbackSelectedSelectors] = useState([]);
  const [wordPressPages, setWordPressPages] = useState([]);
  const [pagesError, setPagesError] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [isFixesApplied, setIsFixesApplied] = useState(false);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [scanContext, setScanContext] = useState(null);
  const [wordPressConnectionsPage, setWordPressConnectionsPage] = useState(1);
  const [wordPressConnectionsHasMore, setWordPressConnectionsHasMore] =
    useState(false);
  const [isLoadingMoreWordPressConnections, setIsLoadingMoreWordPressConnections] =
    useState(false);
  const [shopifyConnectionsPage, setShopifyConnectionsPage] = useState(1);
  const [shopifyConnectionsHasMore, setShopifyConnectionsHasMore] =
    useState(false);
  const [isLoadingMoreShopifyConnections, setIsLoadingMoreShopifyConnections] =
    useState(false);
  const [shopifyExtensionStatus, setShopifyExtensionStatus] = useState(null);
  const [isCheckingShopifyExtension, setIsCheckingShopifyExtension] =
    useState(false);

  // The websites list endpoint is paginated (default limit 10) — fetch one
  // page at a time and load more as the user scrolls the dropdown list.
  const fetchWebsitesPage = async (platform, page) => {
    const response = await getAllWebsites(
      { platform, isActive: true },
      { page },
    );
    const websites = response?.websites || response?.data || [];
    const totalPages = response?.totalPages || 1;
    return { websites, hasMore: page < totalPages };
  };

  // Load the first page of WordPress and Shopify website connections on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setIsLoadingConnections(true);
    Promise.all([
      fetchWebsitesPage("wordpress", 1),
      fetchWebsitesPage("shopify", 1),
    ])
      .then(([wordpressResult, shopifyResult]) => {
        setWordPressConnections(wordpressResult.websites);
        setWordPressConnectionsPage(1);
        setWordPressConnectionsHasMore(wordpressResult.hasMore);

        setShopifyConnections(shopifyResult.websites);
        setShopifyConnectionsPage(1);
        setShopifyConnectionsHasMore(shopifyResult.hasMore);

        if (wordpressResult.websites.length > 0) {
          setSelectedConnection(wordpressResult.websites[0]);
          setSelectedPage(null);
        }

        const connectedShopifyWebsites = shopifyResult.websites.filter(
          (w) => w.connectionStatus === "connected",
        );
        if (connectedShopifyWebsites.length > 0) {
          setSelectedShopifyConnection(connectedShopifyWebsites[0]);
        }
      })
      .catch((error) => {
        console.error("Error loading platform connections:", error);
      })
      .finally(() => {
        setIsLoadingConnections(false);
      });
  }, []); // getAllWebsites is a new ref on every render — run once only

  const handleLoadMoreWordPressConnections = () => {
    if (isLoadingMoreWordPressConnections || !wordPressConnectionsHasMore) return;
    const nextPage = wordPressConnectionsPage + 1;
    setIsLoadingMoreWordPressConnections(true);
    fetchWebsitesPage("wordpress", nextPage)
      .then(({ websites, hasMore }) => {
        setWordPressConnections((prev) => [...prev, ...websites]);
        setWordPressConnectionsPage(nextPage);
        setWordPressConnectionsHasMore(hasMore);
      })
      .catch((error) => {
        console.error("Error loading more WordPress connections:", error);
      })
      .finally(() => {
        setIsLoadingMoreWordPressConnections(false);
      });
  };

  const handleLoadMoreShopifyConnections = () => {
    if (isLoadingMoreShopifyConnections || !shopifyConnectionsHasMore) return;
    const nextPage = shopifyConnectionsPage + 1;
    setIsLoadingMoreShopifyConnections(true);
    fetchWebsitesPage("shopify", nextPage)
      .then(({ websites, hasMore }) => {
        setShopifyConnections((prev) => [...prev, ...websites]);
        setShopifyConnectionsPage(nextPage);
        setShopifyConnectionsHasMore(hasMore);
      })
      .catch((error) => {
        console.error("Error loading more Shopify connections:", error);
      })
      .finally(() => {
        setIsLoadingMoreShopifyConnections(false);
      });
  };

  // Automatically fetch pages when connection is selected
  useEffect(() => {
    const connectionId = selectedConnection?._id || selectedConnection?.id;
    if (connectionId) {
      setPagesError(null);
      getPagesDirectly(connectionId, {
        onSuccess: (response) => {
          const pages = response.data?.pages || response.pages;
          if (pages) {
            setWordPressPages(pages);
          }
        },
        onError: (error) => {
          console.error("Error auto-fetching pages:", error);
          setWordPressPages([]);
          setPagesError(error.message || "Authorization failed");
          // Only toast if it's an auth error to avoid spamming other errors
          if (
            error.message?.includes("401") ||
            error.message?.toLowerCase().includes("authorized") ||
            error.message?.toLowerCase().includes("key")
          ) {
            toast.error(`WordPress Auth Error: ${error.message}`);
          }
        },
      });
    } else {
      setWordPressPages([]);
      setPagesError(null);
      setSelectedPage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnection]);

  useEffect(() => {
    const loadShopifyPages = async () => {
      const shopUrl = selectedShopifyConnection?.siteUrl;
      if (!shopUrl) {
        setShopifyPages([]);
        setSelectedShopifyPage(null);
        setShopifyPagesError(null);
        setShopifyExtensionStatus(null);
        return;
      }

      setIsGettingShopifyPages(true);
      setShopifyPagesError(null);

      const normalizedShop = shopUrl
        .replace(/^https?:\/\//i, "")
        .replace(/\/$/, "");

      const myshopifyDomain =
        selectedShopifyConnection?.myshopifyDomain || normalizedShop;

      // Fetch store pages and the AI accessibility fixer extension status together
      setIsCheckingShopifyExtension(true);
      shopifyService
        .getExtensionStatus(myshopifyDomain)
        .then((status) => {
          setShopifyExtensionStatus({ ...status, siteUrl: normalizedShop });
        })
        .catch((error) => {
          console.error("Error checking Shopify extension status:", error);
          setShopifyExtensionStatus(null);
        })
        .finally(() => {
          setIsCheckingShopifyExtension(false);
        });

      try {
        const response = await fetch(
          `${SENTRYPRIME_URL}/api/accessibility/store-pages?shop=${encodeURIComponent(myshopifyDomain)}`,
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch Shopify pages (${response.status})`);
        }

        const data = await response.json();
        const pages = (data?.pages || []).map((page, index) => ({
          id: `${page.type || "page"}-${index}`,
          title: page.title || page.type || "Shopify Page",
          url: page.url,
          type: page.type,
        }));

        setShopifyPages(pages);
        setSelectedShopifyPage(null);
      } catch (error) {
        console.error("Error loading Shopify pages:", error);
        setShopifyPages([]);
        setSelectedShopifyPage(null);
        setShopifyPagesError(error.message || "Failed to load Shopify pages");
      } finally {
        setIsGettingShopifyPages(false);
      }
    };

    loadShopifyPages();
  }, [selectedShopifyConnection]);

  const showSiteInfoForConnection = (
    connection,
    platformLabel = "WordPress",
  ) => {
    if (!connection) {
      toast.warning(`Please select a ${platformLabel} connection first`);
      return;
    }
    getSiteInfo(connection._id || connection.id, {
      onSuccess: (response) => {
        if (response.success && response.data) {
          const data = response.data;
          const isHTML =
            typeof data === "string" &&
            (data.trim().startsWith("<!DOCTYPE") ||
              data.trim().startsWith("<html"));

          setWpModalTitle("WordPress Site Information");
          if (isHTML) {
            setWpModalContent(<HTMLViewer htmlContent={data} />);
          } else {
            const siteInfo = data.site || data;
            setWpModalContent(<SiteInfoModal siteInfo={siteInfo} />);
          }
          setWpModalOpen(true);
        } else {
          toast.error("Failed to fetch site info");
        }
      },
      onError: (error) => {
        toast.error(
          "Error fetching site info: " +
            (error.response?.data?.message || error.message),
        );
      },
    });
  };
  const showWordPressSiteInfo = () =>
    showSiteInfoForConnection(selectedConnection, "WordPress");

  const handleTestConnection = (connectionId) => {
    testConnection(connectionId, {
      onSuccess: (response) => {
        if (response.success) {
          toast.success("Connection test successful!");
        } else {
          toast.error("Connection test failed");
        }
      },
      onError: (error) => {
        toast.error(
          "Connection test failed: " +
            (error.response?.data?.message || error.message),
        );
      },
    });
  };

  const showScanHistoryForConnection = (
    connection,
    platformLabel = "WordPress",
  ) => {
    if (!connection) {
      toast.warning(`Please select a ${platformLabel} connection first`);
      return;
    }
    const websiteId = connection._id || connection.id;
    navigate(`/website-scans/${websiteId}`);
  };
  const showScanHistory = () =>
    showScanHistoryForConnection(selectedConnection, "WordPress");

  const handleScanPageAccessibilityForConnection = (
    connection,
    page,
    platformLabel = "WordPress",
  ) => {
    if (!connection) {
      toast.warning(`Please select a ${platformLabel} connection first`);
      return;
    }

    const siteUrl = page?.url || connection.siteUrl;
    if (!siteUrl) {
      toast.error("Site URL is missing");
      return;
    }

    const connectionId = connection._id || connection.id;
    const platform = platformLabel.toLowerCase();
    setScanContext({
      connection,
      page,
      platform,
    });

    // Initiate scan with websiteConnectionId
    scanUrl(
      {
        url: siteUrl,
        wcagLevel: "wcag2aa",
        websiteConnectionId: connectionId,
      },
      {
        onSuccess: (response) => {
          // Extract scan data from response
          const scanData = response?.data || response;
          if (scanData) {
            setScanResponseData(scanData);
            setIsFixesApplied(false); // Reset for new scan
            setScanResponseModalOpen(true);
            toast.success("Scan completed successfully!");
          } else {
            toast.success("Scan completed! Redirecting to Scans page...");
            setTimeout(() => {
              navigate("/scans");
            }, 1000);
          }
        },
        onError: (error) => {
          setScanContext(null);
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Failed to initiate scan";
          toast.error(errorMessage);
        },
      },
    );
  };
  const handleScanPageAccessibility = () =>
    handleScanPageAccessibilityForConnection(
      selectedConnection,
      selectedPage,
      "WordPress",
    );

  // Preview the last applied fix set for a connection + page (GET rollback-info),
  // then open the confirmation modal. Shared by every "Rollback Fixes" entry point.
  const handleOpenRollbackPreview = (connection, pageUrl, platformLabel = "WordPress") => {
    if (!connection) {
      toast.warning(`Please select a ${platformLabel} connection first`);
      return;
    }
    if (!pageUrl) {
      toast.warning("No page URL available for rollback");
      return;
    }
    const connectionId = connection._id || connection.id;

    getRollbackInfo(
      { connectionId, pageUrl },
      {
        onSuccess: (response) => {
          if (response.success || response.data) {
            setRollbackConnectionId(connectionId);
            setRollbackPreviewData(response.data || response);
            setRollbackSelectedSelectors([]);
            setShowRollbackModal(true);
          } else {
            toast.error("No applied fixes found to rollback");
          }
        },
        onError: (error) => {
          if (error.response?.status === 404) {
            toast.info("No applied fixes found for this page yet.");
          } else {
            toast.error(
              "Error fetching rollback info: " +
              (error.response?.data?.message || error.message),
            );
          }
        },
      },
    );
  };
  const handleRollbackFixesForConnection = (
    connection,
    page,
    platformLabel = "WordPress",
  ) => handleOpenRollbackPreview(connection, page?.url, platformLabel);

  const handleRollbackFixes = () => {
    const pageUrl = scanResponseData?.url || scanResponseData?.data?.url || selectedPage?.url;
    handleOpenRollbackPreview(selectedConnection, pageUrl);
  };

  const handleConfirmRollback = () => {
    const pageUrl = rollbackPreviewData?.pageUrl || scanResponseData?.url || scanResponseData?.data?.url;
    const connectionId = rollbackConnectionId || selectedConnection?._id || selectedConnection?.id;

    rollbackFixes(
      { connectionId, pageUrl, nestedSelectors: rollbackSelectedSelectors },
      {
        onSuccess: (response) => {
          if (response.success) {
            toast.success("Fixes rolled back successfully!");
            // We're on the Integrations page (no persisted scan record to refetch
            // by ID here) — re-run the scan itself so the results shown reflect
            // the page's reverted, live HTML.
            if (pageUrl && connectionId) {
              scanUrl(
                { url: pageUrl, wcagLevel: "wcag2aa", websiteConnectionId: connectionId },
                {
                  onSuccess: (scanResponse) => {
                    setScanResponseData(scanResponse?.data || scanResponse);
                  },
                },
              );
            }
            setIsFixesApplied(false);
          } else {
            toast.error("Failed to rollback fixes");
          }
          setShowRollbackModal(false);
          setRollbackPreviewData(null);
          setRollbackConnectionId(null);
          setRollbackSelectedSelectors([]);
        },
        onError: (error) => {
          toast.error(
            "Error rolling back fixes: " +
            (error.response?.data?.message || error.message),
          );
        },
      },
    );
  };

  const handleEnableShopifyExtension = () => {
    if (!shopifyExtensionStatus) return;
    const { shop, siteUrl, theme, block } = shopifyExtensionStatus;
    const shopDomain = shop || siteUrl;
    if (!shopDomain || !theme?.id || !block?.type) {
      window.open(
        `${SENTRYPRIME_URL}/authenticate?shop=${encodeURIComponent(shopDomain || "")}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }

    // block.type looks like: shopify://apps/<handle>/blocks/<block-handle>/<uuid>
    const parts = block.type.split("/");
    const uuid = parts[parts.length - 1];
    const blockHandle = parts[parts.length - 2];
    const activateAppId = `${uuid}/${blockHandle}`;

    window.open(
      `https://${shopDomain}/admin/themes/${theme.id}/editor?context=apps&activateAppId=${encodeURIComponent(activateAppId)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleGetAiFixes = () => {
    if (!scanResponseData) {
      toast.warning("No scan data available");
      return;
    }

    const data = scanResponseData?.data || scanResponseData;
    const violations = data.violations || [];

    if (violations.length === 0) {
      toast.info("No violations found to fix");
      return;
    }

    // Prepare the cleaned payload
    const payloadViolations = violations.map((v) => ({
      id: v.id,
      impact: v.impact,
      nodes: (v.nodes || []).map((n) => {
        const fallbackSelector = Array.isArray(n.target)
          ? n.target.join(" > ")
          : n.target;
        const node = {
          html: n.html,
          target: n.target,
          nestedSelector:
            n.nestedSelector || n.selector || fallbackSelector || "html",
        };
        if (n.hierarchy) node.hierarchy = n.hierarchy;
        // Color-contrast fields
        if (n.contrastRatio) node.contrastRatio = n.contrastRatio;
        if (n.requiredRatio) node.requiredRatio = n.requiredRatio;
        if (n.isLargeText !== undefined) node.isLargeText = n.isLargeText;
        // Color-contrast composition metadata (gradients / opacity-faded text).
        // Forward whenever the scan reports them; omit when not applicable.
        if (n.isGradient !== undefined) node.isGradient = n.isGradient;
        if (n.effectiveOpacity !== undefined)
          node.effectiveOpacity = n.effectiveOpacity;
        if (n.opacityCeiling !== undefined)
          node.opacityCeiling = n.opacityCeiling;
        if (n.fixScope !== undefined) node.fixScope = n.fixScope;
        // computedStyle is forwarded untouched, so its new keys
        // (backgroundImage, opacity) pass straight through to the fixer.
        if (n.computedStyle) node.computedStyle = n.computedStyle;
        return node;
      }),
    }));

    const activeConnection = scanContext?.connection || selectedConnection;
    const activePage = scanContext?.page || selectedPage;
    const activePlatform = (
      scanContext?.platform ||
      activeConnection?.platform ||
      "wordpress"
    ).toLowerCase();
    const activeApiKey =
      activeConnection?.apiKey ||
      activeConnection?.metadata?.shopifyInstallPayload?.accessToken;

    if (!activeConnection) {
      toast.warning("No website connection selected");
      return;
    }

    if (!activeApiKey) {
      toast.error("API key missing for selected website connection");
      return;
    }

    const aiFixesPayload = {
      violations: payloadViolations,
      apiKey: activeApiKey,
      pageUrl: scanResponseData?.url || scanResponseData?.data?.url,
      baseUrl: activeConnection?.siteUrl,
      platform: activePlatform,
      scanResultId:
        scanResponseData?._id ||
        scanResponseData?.data?._id ||
        scanResponseData?.scanResultId ||
        scanResponseData?.data?.scanResultId ||
        scanResponseData?.resultId ||
        scanResponseData?.data?.resultId,
    };

    if (activePlatform === "wordpress") {
      aiFixesPayload.pageBuilder = activePage?.page_builder;
      aiFixesPayload.pageId = activePage?.id;
    }

    getAiFixes(
      aiFixesPayload,
      {
onSuccess: (response) => {
           if (response.success && response.data) {
             const aiFixesList = response.data.fixes || [];

             setScanResponseData((prev) => {
               const prevData = prev?.data || prev;
               const updatedViolations = (prevData.violations || []).map((v) => {
                 const nodeFixes = aiFixesList.filter(
                   (f) => f.violationId === v.id,
                 );
                 if (nodeFixes.length > 0) {
                   return {
                     ...v,
                     aiFixes: nodeFixes,
                     suggestedFix: nodeFixes[0].fixedHtml,
                   };
                 }
                 return v;
               });

               // Per the API contract, get-ai-fixes pushes the fixes to the live
               // site and persists the snapshot server-side on success — use the
               // server's own appliedFixes when present, otherwise fall back to
               // building it from the returned fix list.
               const appliedFixes = response.data.appliedFixes || {
                 pageUrl: prevData.url || prev?.url,
                 fixes: aiFixesList.map((f) => ({
                   violationId: f.violationId,
                   nestedSelector: f.nestedSelector,
                   target: f.target,
                   impact: f.impact,
                   originalHtml: f.originalHtml,
                   fixedHtml: f.fixedHtml,
                 })),
                 appliedAt: new Date().toISOString(),
               };

               return {
                 ...prev,
                 data: { ...prevData, violations: updatedViolations, appliedFixes },
               };
             });

             setIsFixesApplied(true);
             toast.success("AI fixes generated and applied successfully!");
             setScanResponseModalOpen(false);
             setScanResponseData(null);
             setIsFixesApplied(false);
             setScanContext(null);
           } else {
             toast.error("Failed to fetch AI fixes");
           }
         },
        onError: (error) => {
          toast.error(
            "Error fetching AI fixes: " +
              (error.response?.data?.message || error.message),
          );
        },
      },
    );
  };

  const handleGetViolationsForConnection = (
    connection,
    page,
    platformLabel = "WordPress",
  ) => {
    if (!connection) {
      toast.warning(`Please select a ${platformLabel} connection first`);
      return;
    }
    getViolations(
      {
        connectionId: connection._id || connection.id,
        pageId: page?.id,
        pageUrl: page?.url,
      },
      {
        onSuccess: (response) => {
          if (response.success && response.data) {
            const data = response.data;
            const isHTML =
              typeof data === "string" &&
              (data.trim().startsWith("<!DOCTYPE") ||
                data.trim().startsWith("<html"));

            if (isHTML) {
              setWpModalTitle("Violations");
              setWpModalContent(<HTMLViewer htmlContent={data} />);
            } else {
              const violations = data.violations || [];
              const total = data.total || violations.length;
              setWpModalTitle(`Violations (${total} scans)`);
              setWpModalContent(
                <ViolationsModal violationsData={violations} />,
              );
            }
            setWpModalOpen(true);
          } else {
            toast.error("Failed to fetch violations");
          }
        },
        onError: (error) => {
          toast.error(
            "Error fetching violations: " +
              (error.response?.data?.message || error.message),
          );
        },
      },
    );
  };
  const handleGetViolations = () =>
    handleGetViolationsForConnection(
      selectedConnection,
      selectedPage,
      "WordPress",
    );

  const handleDisconnectSiteForConnection = (
    connection,
    platformLabel = "WordPress",
  ) => {
    if (!connection) {
      toast.warning(`Please select a ${platformLabel} connection first`);
      return;
    }
    setDisconnectTargetConnection(connection);
    setShowDisconnectDialog(true);
  };
  const handleDisconnectSite = () =>
    handleDisconnectSiteForConnection(selectedConnection, "WordPress");

  const handleConfirmDisconnect = () => {
    if (!disconnectTargetConnection) return;

    disconnectSite(
      disconnectTargetConnection._id || disconnectTargetConnection.id,
      {
        onSuccess: (response) => {
          if (response.success) {
            toast.success("Site disconnected successfully!");
            setWordPressPages([]);
            fetchWebsitesPage("wordpress", 1)
              .then(({ websites, hasMore }) => {
                setWordPressConnections(websites);
                setWordPressConnectionsPage(1);
                setWordPressConnectionsHasMore(hasMore);
                setSelectedConnection(websites.length > 0 ? websites[0] : null);
              })
              .catch((error) => {
                console.error("Error reloading connections:", error);
              });
          } else {
            toast.error("Failed to disconnect site");
          }
        },
        onError: (error) => {
          toast.error(
            "Error disconnecting site: " +
              (error.response?.data?.message || error.message),
          );
        },
      },
    );
  };

  return (
    <PageContainer
      title="Platform Integrations"
      subtitle="Connect your websites for automated accessibility monitoring"
    >
      <WordPressGuideBanner />

      <WordPressActionsCard
        availableActions={availableActions}
        isLoadingActions={isLoadingActions}
        connectionError={connectionError}
        onGoToWebsiteConnections={() => navigate("/website-connections")}
      />

      {isLoadingConnections ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-500">
            Loading platform connections...
          </span>
        </div>
      ) : (
        <WordPressConnectionsList
          connections={wordPressConnections}
          selectedConnection={selectedConnection}
          onSelectConnection={setSelectedConnection}
          onGoToWebsiteConnections={() => navigate("/website-connections")}
          pages={wordPressPages}
          selectedPage={selectedPage}
          onSelectPage={setSelectedPage}
          isGettingPages={isGettingPagesDirect}
          pagesError={pagesError}
          onLoadMoreConnections={handleLoadMoreWordPressConnections}
          hasMoreConnections={wordPressConnectionsHasMore}
          isLoadingMoreConnections={isLoadingMoreWordPressConnections}
        />
      )}

      {selectedConnection && (
        <div className="mb-6">
          <WordPressConnectionCard
            connection={selectedConnection}
            selectedPage={selectedPage}
            onTestConnection={handleTestConnection}
            onShowSiteInfo={showWordPressSiteInfo}
            onShowScanHistory={showScanHistory}
            onScanPageAccessibility={handleScanPageAccessibility}
            onRollbackFixes={handleRollbackFixes}
            onGetViolations={handleGetViolations}
            onDisconnectSite={handleDisconnectSite}
            isTestingConnection={isTestingConnection}
            isGettingSiteInfo={isGettingSiteInfo}
            isGettingPages={isGettingPagesDirect}
            isGettingScanHistory={false}
            isScanningPageAccessibility={isScanningPageAccessibility}
            isRollingBack={isRollingBack}
            isGettingRollbackInfo={isGettingRollbackInfo}
            isGettingViolations={isGettingViolations}
            isDisconnectingSite={isDisconnectingSite}
          />
        </div>
      )}

      <ShopifyCard
        onGoToWebsiteConnections={() => navigate("/website-connections")}
      />

      {!isLoadingConnections && (
        <WordPressConnectionsList
          connections={shopifyConnections.filter((c) => c.connectionStatus === "connected")}
          selectedConnection={selectedShopifyConnection}
          onSelectConnection={setSelectedShopifyConnection}
          onGoToWebsiteConnections={() => navigate("/website-connections")}
          platformName="Shopify"
          title="Shopify Connections"
          icon="logos:shopify"
          iconClassName="text-6xl"
          showPageSelector
          pages={shopifyPages}
          selectedPage={selectedShopifyPage}
          onSelectPage={setSelectedShopifyPage}
          isGettingPages={isGettingShopifyPages}
          pagesError={shopifyPagesError}
          onLoadMoreConnections={handleLoadMoreShopifyConnections}
          hasMoreConnections={shopifyConnectionsHasMore}
          isLoadingMoreConnections={isLoadingMoreShopifyConnections}
        />
      )}

      {selectedShopifyConnection && (
        <div className="mb-6">
          <WordPressConnectionCard
            connection={selectedShopifyConnection}
            selectedPage={selectedShopifyPage}
            onTestConnection={handleTestConnection}
            onShowSiteInfo={() =>
              showSiteInfoForConnection(selectedShopifyConnection, "Shopify")
            }
            onShowScanHistory={() =>
              showScanHistoryForConnection(selectedShopifyConnection, "Shopify")
            }
            onScanPageAccessibility={() =>
              handleScanPageAccessibilityForConnection(
                selectedShopifyConnection,
                selectedShopifyPage,
                "Shopify",
              )
            }
            onRollbackFixes={() =>
              handleRollbackFixesForConnection(
                selectedShopifyConnection,
                selectedShopifyPage,
                "Shopify",
              )
            }
            onGetViolations={() =>
              handleGetViolationsForConnection(
                selectedShopifyConnection,
                selectedShopifyPage,
                "Shopify",
              )
            }
            onDisconnectSite={() =>
              handleDisconnectSiteForConnection(
                selectedShopifyConnection,
                "Shopify",
              )
            }
            isTestingConnection={isTestingConnection}
            isGettingSiteInfo={isGettingSiteInfo}
            isGettingPages={isGettingShopifyPages}
            isGettingScanHistory={false}
            isScanningPageAccessibility={isScanningPageAccessibility}
            isRollingBack={isRollingBack}
            isGettingRollbackInfo={isGettingRollbackInfo}
            isGettingViolations={isGettingViolations}
            isDisconnectingSite={isDisconnectingSite}
            shopifyExtensionStatus={shopifyExtensionStatus}
            isCheckingShopifyExtension={isCheckingShopifyExtension}
            onEnableShopifyExtension={handleEnableShopifyExtension}
          />
        </div>
      )}

      <WordPressModals
        isOpen={wpModalOpen}
        onClose={() => setWpModalOpen(false)}
        title={wpModalTitle}
        content={wpModalContent}
      />

      <WordPressModals
        isOpen={scanResponseModalOpen}
        onClose={() => {
          setScanResponseModalOpen(false);
          setScanResponseData(null);
          setIsFixesApplied(false);
          setScanContext(null);
        }}
        title="Complete Scan Response"
        content={
          scanResponseData ? (
            <CompleteScanResponseModal
              scanData={scanResponseData}
              connection={scanContext?.connection || selectedConnection}
              onGetFixes={handleGetAiFixes}
              isGettingFixes={isGettingAiFixes}
              isFixesApplied={isFixesApplied}
              isRescanning={isScanningPageAccessibility}
              shopifyExtensionStatus={shopifyExtensionStatus}
              isCheckingShopifyExtension={isCheckingShopifyExtension}
              onEnableShopifyExtension={handleEnableShopifyExtension}
            />
          ) : null
        }
        size="xl"
      />

      <ConfirmDialog
        isOpen={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        onConfirm={handleConfirmDisconnect}
        title="Disconnect WordPress Site"
        message={
          disconnectTargetConnection
            ? `Are you sure you want to disconnect "${disconnectTargetConnection.siteName}"? This action cannot be undone.`
            : "Are you sure you want to disconnect this site?"
        }
        confirmText="Disconnect"
        cancelText="Cancel"
        variant="danger"
        icon="mdi:link-off"
      />

      <Modal
        isOpen={showRollbackModal}
        onClose={() => {
          setShowRollbackModal(false);
          setRollbackPreviewData(null);
          setRollbackConnectionId(null);
          setRollbackSelectedSelectors([]);
        }}
        title="Rollback Preview"
        size="lg"
      >
        <div className="py-2">
          {rollbackPreviewData?.fixes?.length > 0 ? (
            <>
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700 flex items-center gap-2">
                  <Icon icon="mdi:alert" className="text-lg" />
                  {rollbackSelectedSelectors.length > 0
                    ? `Rolling back will restore the original HTML for ${rollbackSelectedSelectors.length} selected element(s).`
                    : `Rolling back will restore the original HTML for all ${rollbackPreviewData.fixes.length} element(s). Check specific items below to roll back only those.`}
                </p>
              </div>
              <label className="flex items-center gap-2 mb-2 px-1 text-xs text-gray-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rollbackSelectedSelectors.length === rollbackPreviewData.fixes.length}
                  onChange={() =>
                    setRollbackSelectedSelectors((prev) =>
                      prev.length === rollbackPreviewData.fixes.length
                        ? []
                        : rollbackPreviewData.fixes.map((f) => f.nestedSelector),
                    )
                  }
                  className="rounded border-gray-300"
                />
                {rollbackSelectedSelectors.length > 0
                  ? `${rollbackSelectedSelectors.length} of ${rollbackPreviewData.fixes.length} selected`
                  : "Select all"}
              </label>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {rollbackPreviewData.fixes.map((fix, index) => (
                  <div key={index} className="p-3 bg-white border border-gray-200 rounded-lg flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={rollbackSelectedSelectors.includes(fix.nestedSelector)}
                      onChange={() =>
                        setRollbackSelectedSelectors((prev) =>
                          prev.includes(fix.nestedSelector)
                            ? prev.filter((s) => s !== fix.nestedSelector)
                            : [...prev, fix.nestedSelector],
                        )
                      }
                      className="mt-1 rounded border-gray-300 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-600 mb-1">
                        {fix.violationId} • {fix.impact} severity
                      </p>
                      <p className="text-[10px] text-gray-400 font-mono mb-2 truncate">
                        {fix.nestedSelector}
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <p className="text-[10px] font-semibold text-red-600 mb-1">Original:</p>
                          <code className="block bg-red-50 p-2 rounded text-red-700 overflow-x-auto">
                            {fix.originalHtml}
                          </code>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold text-green-600 mb-1">Fixed:</p>
                          <code className="block bg-green-50 p-2 rounded text-green-700 overflow-x-auto">
                            {fix.fixedHtml}
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-4 justify-end">
                <Button variant="secondary" onClick={() => { setShowRollbackModal(false); setRollbackPreviewData(null); setRollbackConnectionId(null); setRollbackSelectedSelectors([]); }} disabled={isRollingBack}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleConfirmRollback} disabled={isRollingBack}>
                  {isRollingBack
                    ? "Rolling back..."
                    : rollbackSelectedSelectors.length > 0
                      ? `Rollback Selected (${rollbackSelectedSelectors.length})`
                      : "Rollback All Fixes"}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500 text-center py-8">No fixes found to rollback.</p>
          )}
        </div>
      </Modal>

      <ScanPlanner
        platformName="Multi-Platform"
        connections={[...wordPressConnections, ...shopifyConnections]}
        selectedConnection={selectedConnection || selectedShopifyConnection}
        onSelectConnection={(conn) => {
          const platform = conn?.platform?.toLowerCase();
          if (platform === 'wordpress') {
            setSelectedConnection(conn);
            setSelectedShopifyConnection(null);
          } else if (platform === 'shopify') {
            setSelectedShopifyConnection(conn);
            setSelectedConnection(null);
          } else {
            setSelectedConnection(conn);
          }
        }}
        pages={selectedConnection ? wordPressPages : shopifyPages}
        selectedPage={selectedConnection ? selectedPage : selectedShopifyPage}
        onSelectPage={(page) => {
          if (selectedConnection) {
            setSelectedPage(page);
          } else if (selectedShopifyConnection) {
            setSelectedShopifyPage(page);
          }
        }}
        isGettingPages={selectedConnection ? isGettingPagesDirect : isGettingShopifyPages}
        pagesError={selectedConnection ? pagesError : shopifyPagesError}
      />
    </PageContainer>
  );
};

export default Integrations;
